/**
 * Play-scoped live client (FR-LIVE-01…05).
 *
 * `useConnectionHealth` already owns the heartbeat and the green/amber/red
 * indicator. This module owns the other half of the same socket: the DOMAIN
 * messages a play screen has to react to. Keeping them apart means the archive
 * never has to know about ping cadence, and the health indicator never has to
 * know about conflicts.
 *
 * The contract on the wire is `@gridiron/shared`'s `LiveMessage` discriminated
 * union (`{ type, payload, traceId }`) — nothing is re-typed here, only
 * narrowed to the five play/event messages the archive consumes:
 *
 * | Message                        | Archive reaction                        |
 * |--------------------------------|-----------------------------------------|
 * | `game.play.created`            | a new play exists → refetch the page    |
 * | `game.play.updated`            | active version moved → refetch the row  |
 * | `game.play.version_added`      | another operator submitted → refetch    |
 * | `game.play.conflict_detected`  | badge appears, live                     |
 * | `game.play.conflict_resolved`  | badge clears, panels close              |
 *
 * Reconnect is exponential-backoff (2s → 30s), the socket is closed on unmount,
 * and `createLiveSocket` returns `null` outside a browser, so a test that does
 * not inject a factory simply runs with `status: 'offline'`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { CONNECTION_HEALTH } from './connection-health-machine';
import {
  createLiveSocket,
  createLiveSseSocket,
  parseLiveFrame,
  resolveApiBase,
  resolveLiveSocketUrl,
  resolveLiveSseUrl,
  type HealthSocket,
  type HealthSocketFactory,
} from './live-socket';
import {
  createLiveTierProbe,
  LIVE_TIER_PROBE_THROTTLE_MS,
  LIVE_TIER_REPROBE_MS,
  type LiveTierProbe,
} from './live-tier';

export type { HealthSocket, HealthSocketFactory } from './live-socket';

/**
 * Mirror of `LIVE_MESSAGE` in `packages/shared/src/live-messages.ts`.
 *
 * `@gridiron/web` deliberately has no workspace dependency on the service kit —
 * the browser bundle must not pull in Express/pino transitively — so the wire
 * contract is restated here, exactly as `live-socket.ts` restates the frame
 * envelope. Keep the two in step; the shared module is the authority.
 */
export const LIVE_MESSAGE = {
  PLAY_CREATED: 'game.play.created',
  PLAY_UPDATED: 'game.play.updated',
  PLAY_DELETED: 'game.play.deleted',
  PLAY_VERSION_ADDED: 'game.play.version_added',
  PLAY_CONFLICT_DETECTED: 'game.play.conflict_detected',
  PLAY_CONFLICT_RESOLVED: 'game.play.conflict_resolved',
} as const;

export interface PlayCreatedPayload {
  play_id: string;
  event_id: string;
  play_number: number;
  created_by: string | null;
  stat_payload: Record<string, unknown>;
}

export interface PlayUpdatedPayload {
  play_id: string;
  event_id: string;
  active_version_id: string;
  success_measure: boolean | null;
  stat_payload_summary: Record<string, unknown>;
  /** Present when the update was a team-wide star toggle. */
  is_favorited?: boolean;
}

export interface PlayDeletedPayload {
  play_id: string;
  event_id: string;
  play_number: number;
}

export interface PlayVersionAddedPayload {
  play_id: string;
  event_id: string;
  version_id: string;
  submitter_role: string;
  is_new_active: boolean;
  triggered_conflict: boolean;
}

export interface PlayConflictDetectedPayload {
  play_id: string;
  event_id: string;
  play_number: number;
  version_count: number;
  submitting_roles: string[];
  active_version_id: string;
  differing_fields: string[];
}

export interface PlayConflictResolvedPayload {
  play_id: string;
  event_id: string;
  resolved_by: string | null;
  resolved_by_role: string;
  resolution_type: 'selected_existing' | 'custom_override';
  winning_version_id: string;
  hidden_version_ids: string[];
  note: string | null;
}

/** Reconnect backoff. */
const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30_000;

/** The subset of `LiveMessage` a play screen subscribes to. */
export const PLAY_LIVE_MESSAGE_TYPES = [
  LIVE_MESSAGE.PLAY_CREATED,
  LIVE_MESSAGE.PLAY_UPDATED,
  LIVE_MESSAGE.PLAY_DELETED,
  LIVE_MESSAGE.PLAY_VERSION_ADDED,
  LIVE_MESSAGE.PLAY_CONFLICT_DETECTED,
  LIVE_MESSAGE.PLAY_CONFLICT_RESOLVED,
] as const;

export type PlayLiveMessageType = (typeof PLAY_LIVE_MESSAGE_TYPES)[number];

/** Discriminated union delivered to `usePlayArchiveSubscription`'s callback. */
export type PlayArchiveEvent =
  | { type: typeof LIVE_MESSAGE.PLAY_CREATED; payload: PlayCreatedPayload }
  | { type: typeof LIVE_MESSAGE.PLAY_UPDATED; payload: PlayUpdatedPayload }
  | { type: typeof LIVE_MESSAGE.PLAY_DELETED; payload: PlayDeletedPayload }
  | { type: typeof LIVE_MESSAGE.PLAY_VERSION_ADDED; payload: PlayVersionAddedPayload }
  | { type: typeof LIVE_MESSAGE.PLAY_CONFLICT_DETECTED; payload: PlayConflictDetectedPayload }
  | { type: typeof LIVE_MESSAGE.PLAY_CONFLICT_RESOLVED; payload: PlayConflictResolvedPayload };

/**
 * What the "Live / Sync… / Offline" subtitle renders from. `polling` is the
 * Free-tier fallback (FR-LIVE-02): the socket is tier-blocked and the screen
 * refreshes over REST every 5 seconds instead.
 */
export type LiveStatus = 'connecting' | 'live' | 'offline' | 'polling';

/**
 * A tier-blocked live channel is NOT an error for the person using the app:
 * the screen quietly refreshes over REST every 5 seconds and shows its own
 * slim "Auto-Refresh · 5s" status. No upgrade modal is raised for it (owner
 * ruling — Free-tier coaches kept hitting "Plan limit reached" while simply
 * clicking around). Plan comparison stays reachable from Billing.
 */

export interface UsePlayArchiveSubscriptionOptions {
  /** `null` suspends the subscription — no event selected, nothing to watch. */
  eventId: string | null;
  onEvent: (event: PlayArchiveEvent) => void;
  /**
   * Called on every tick of the Free-tier polling fallback (FR-LIVE-02).
   * Screens pass their refetch here; when the live channel is tier-blocked the
   * hook invokes it every `retryAfter` (5) seconds instead of reconnecting.
   */
  onPoll?: () => void;
  isEnabled?: boolean;
  /** Absolute ws(s):// URL. Defaults to {@link resolveLiveSocketUrl}. */
  url?: string;
  /** Absolute http(s):// SSE URL. Defaults to {@link resolveLiveSseUrl}. */
  sseUrl?: string;
  /** Injectable for tests; the default returns `null` outside a browser. */
  socketFactory?: HealthSocketFactory;
  /** Fallback transport factory; defaults to the browser `EventSource`. */
  sseSocketFactory?: HealthSocketFactory;
  /** Injectable tier probe; defaults to fetching the SSE endpoint. */
  tierProbe?: LiveTierProbe;
}

export interface UsePlayArchiveSubscriptionResult {
  status: LiveStatus;
  /** Domain messages delivered since mount — a cheap "did it arrive?" probe. */
  receivedCount: number;
}

function isPlayMessageType(value: string): value is PlayLiveMessageType {
  return (PLAY_LIVE_MESSAGE_TYPES as readonly string[]).includes(value);
}

/**
 * Narrow a raw frame to a play message.
 *
 * `parseLiveFrame` already guarantees `{ type, payload }`; this only rejects
 * the heartbeat/control frames and the stats/event messages other screens own.
 */
export function toPlayArchiveEvent(frame: unknown): PlayArchiveEvent | null {
  if (typeof frame !== 'object' || frame === null) return null;
  const candidate = frame as { type?: unknown; payload?: unknown };
  if (typeof candidate.type !== 'string' || !isPlayMessageType(candidate.type)) return null;
  if (typeof candidate.payload !== 'object' || candidate.payload === null) return null;
  return { type: candidate.type, payload: candidate.payload } as PlayArchiveEvent;
}

/**
 * Subscribe to the live channel for one event and hand every play message to
 * `onEvent`. The callback is read through a ref, so a caller may pass an inline
 * closure without tearing the socket down on every render.
 */
export function usePlayArchiveSubscription(
  options: UsePlayArchiveSubscriptionOptions
): UsePlayArchiveSubscriptionResult {
  const {
    eventId,
    onEvent,
    onPoll,
    isEnabled = true,
    url,
    sseUrl,
    socketFactory = createLiveSocket,
    sseSocketFactory = createLiveSseSocket,
    tierProbe,
  } = options;
  const [status, setStatus] = useState<LiveStatus>('offline');
  const [receivedCount, setReceivedCount] = useState(0);

  const callbackRef = useRef(onEvent);
  callbackRef.current = onEvent;
  const pollRef = useRef(onPoll);
  pollRef.current = onPoll;
  const factoryRef = useRef(socketFactory);
  factoryRef.current = socketFactory;
  const sseFactoryRef = useRef(sseSocketFactory);
  sseFactoryRef.current = sseSocketFactory;
  const tierProbeRef = useRef(tierProbe);
  tierProbeRef.current = tierProbe;

  const socketUrl = useMemo(
    () => url ?? resolveLiveSocketUrl(resolveApiBase(), eventId),
    [eventId, url]
  );
  const sseSocketUrl = useMemo(
    () => sseUrl ?? resolveLiveSseUrl(resolveApiBase(), eventId),
    [eventId, sseUrl]
  );
  const isActive = isEnabled && eventId !== null;

  useEffect(() => {
    if (!isActive) {
      setStatus('offline');
      return undefined;
    }

    let socket: HealthSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let isCancelled = false;
    let attempts = 0;
    // Offline since when — armed at mount, cleared on open. Once the outage
    // outlives the fallback window, reconnect attempts use the SSE transport
    // (same policy as `useConnectionHealth`).
    let downSince: number | null = Date.now();
    let isSseUnavailable = false;

    // Free-tier polling fallback (FR-LIVE-02). The probe asks the SSE endpoint
    // whether the connect failures are a 402 tier rejection; if so, reconnect
    // stops and `onPoll` fires every `retryAfter` seconds until a periodic
    // re-probe reports the plan now includes the live channel.
    const defaultProbe = createLiveTierProbe(sseSocketUrl);
    const probe: LiveTierProbe = () => (tierProbeRef.current ?? defaultProbe)();
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let reprobeTimer: ReturnType<typeof setTimeout> | null = null;
    let isProbing = false;
    let lastProbeAtMs: number | null = null;

    const scheduleReprobe = (): void => {
      if (isCancelled) return;
      reprobeTimer = setTimeout(() => {
        reprobeTimer = null;
        void probe().then((result) => {
          if (isCancelled || pollTimer === null) return;
          if (result.isBlocked) {
            scheduleReprobe();
            return;
          }
          // Upgraded mid-session: leave polling and dial the socket again.
          clearInterval(pollTimer);
          pollTimer = null;
          attempts = 0;
          downSince = Date.now();
          open();
        });
      }, LIVE_TIER_REPROBE_MS);
    };

    const enterPolling = (retryAfterSeconds: number): void => {
      if (isCancelled || pollTimer !== null) return;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      const superseded = socket;
      socket = null;
      superseded?.close();
      setStatus('polling');
      pollRef.current?.();
      pollTimer = setInterval(() => pollRef.current?.(), Math.max(1000, retryAfterSeconds * 1000));
      scheduleReprobe();
    };

    const maybeProbeTier = (): void => {
      if (isCancelled || isProbing || pollTimer !== null) return;
      const at = Date.now();
      if (lastProbeAtMs !== null && at - lastProbeAtMs < LIVE_TIER_PROBE_THROTTLE_MS) return;
      lastProbeAtMs = at;
      isProbing = true;
      void probe().then((result) => {
        isProbing = false;
        if (isCancelled || !result.isBlocked) return;
        enterPolling(result.retryAfterSeconds);
      });
    };

    const open = (): void => {
      if (isCancelled) return;
      setStatus('connecting');
      const viaSse =
        !isSseUnavailable &&
        downSince !== null &&
        Date.now() - downSince >= CONNECTION_HEALTH.sseFallbackAfterMs;
      socket = (viaSse ? sseFactoryRef.current : factoryRef.current)(
        viaSse ? sseSocketUrl : socketUrl,
        {
          onOpen: () => {
            attempts = 0;
            downSince = null;
            setStatus('live');
          },
          onClose: () => {
            if (pollTimer !== null) return; // superseded by the polling fallback
            if (downSince === null) downSince = Date.now();
            setStatus('offline');
            // A connect attempt that never opened may be a 402 tier rejection,
            // which the socket APIs cannot surface — ask over plain HTTP.
            maybeProbeTier();
            if (reconnectTimer !== null || isCancelled) return;
            const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempts, RECONNECT_MAX_MS);
            attempts += 1;
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null;
              open();
            }, delay);
          },
          onError: () => setStatus('offline'),
          onMessage: (data) => {
            const message = toPlayArchiveEvent(parseLiveFrame(data));
            if (message === null) return;
            setReceivedCount((count) => count + 1);
            callbackRef.current(message);
          },
        }
      );
      if (socket === null) {
        if (viaSse) {
          // No EventSource in this runtime — retry on the primary transport.
          isSseUnavailable = true;
          open();
          return;
        }
        // No WebSocket in this runtime (SSR, or a test with no injected factory).
        setStatus('offline');
      }
    };

    open();
    return () => {
      isCancelled = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      if (pollTimer !== null) clearInterval(pollTimer);
      if (reprobeTimer !== null) clearTimeout(reprobeTimer);
      socket?.close();
    };
  }, [isActive, socketUrl, sseSocketUrl]);

  return { status, receivedCount };
}
